import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Shield,
  Clock,
  MapPin,
  Phone,
  Star,
  ArrowRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { ContactForm } from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { OurWorkSection } from "@/components/OurWorkSection";

export interface ServicePageProps {
  title: string;
  keyword: string;
  metaTitle: string;
  metaDesc: string;
  heroSub: string;
  heroImg: string;
  trustImg: string;
  seoImg: string;
  faqImg: string;
  trustIntro: string;
  slug: string;
  showOurWork?: boolean;
}

const allServices = [
  {
    id: "haushaltsaufloesung-berlin",
    title: "Haushaltsauflösung",
    desc: "Komplette Räumung vom Keller bis zum Dachboden, inklusive Demontage.",
  },
  {
    id: "entruempelung-berlin",
    title: "Entrümpelung",
    desc: "Schnelle Befreiung von Sperrmüll und Unrat mit fachgerechter Entsorgung.",
  },
  {
    id: "nachlassverwertung-berlin",
    title: "Nachlassverwertung",
    desc: "Sichtung und faire Anrechnung von Wertsachen aus dem Nachlass.",
  },
  {
    id: "bueroaufloesung-berlin",
    title: "Büroauflösung",
    desc: "Demontage von IT und Möbeln sowie zertifizierte Aktenvernichtung.",
  },
  {
    id: "wohnungsaufloesung-berlin",
    title: "Wohnungsauflösung",
    desc: "Besenreine oder auf Wunsch grundgereinigte Übergabe der Räume.",
  },
  {
    id: "geschaeftsaufloesung-berlin",
    title: "Geschäftsauflösung",
    desc: "Strukturierte Räumung von Ladenlokalen und Werkstätten.",
  },
];

const getPricingContent = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("haushaltsauflösung")) {
    return {
      intro: `Wahrscheinlich die Frage, die Ihnen schon seit der Überschrift auf der Zunge brennt: <strong>Was kostet eine ${title} in Berlin eigentlich?</strong> Bei Miros Haushaltsauflösung starten unsere Preise fair kalkuliert. Natürlich kann es je nach Größe des Haushalts und Aufwand variieren, aber unser Festpreis bleibt in jedem Fall transparent.`,
      example: `Sie haben eine komplette Wohnung in Berlin aufzulösen. Anhand Ihrer Fotos oder einer kurzen Besichtigung kalkulieren wir genau und nennen Ihnen einen fairen Festpreis. In diesem Preis sind sämtliche Kosten für Sortierung, Transport, Entsorgung und besenreine Übergabe enthalten.`,
      why: `Wir arbeiten effizient und diskret. Wir sind eingespielt, kennen die Recyclinghöfe in Berlin und verwerten noch Brauchbares. Das schont die Umwelt und senkt oft Ihre Kosten.`,
      tip: `Fragen Sie einfach kostenfrei und unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein transparentes Angebot für Ihre ${title} in Berlin zusammen.`,
    };
  }
  if (t.includes("entrümpelung")) {
    return {
      intro: `Oft werden wir als Erstes gefragt: <strong>Mit welchen Kosten muss ich für eine ${title} in Berlin rechnen?</strong> Bei uns gibt es keine versteckten Gebühren. Jeder Auftrag ist individuell, aber Sie erhalten von uns immer ein klares und verlässliches Festpreisangebot.`,
      example: `Sie haben einen vollen Keller, Dachboden oder eine vermüllte Wohnung, die geräumt werden muss. Nach einer kurzen Einschätzung erhalten Sie unseren Festpreis, der Abbau, Abtransport und fachgerechte Entsorgung komplett abdeckt.`,
      why: `Dank unserer langjährigen Erfahrung wissen wir genau, wie wir schnell und strukturiert vorgehen. Wir trennen Wertstoffe fachgerecht und arbeiten mit festen Routen in Berlin – das spart Zeit und Geld.`,
      tip: `Zögern Sie nicht und holen Sie sich ein kostenloses Angebot ein. Wir geben Ihnen schnell eine klare Preiseinschätzung für Ihre ${title}.`,
    };
  }
  if (t.includes("wohnungsauflösung")) {
    return {
      intro: `Eine der wichtigsten Fragen in einer oft stressigen Zeit: <strong>Was kostet eine ${title} in Berlin?</strong> Wir legen großen Wert auf faire und nachvollziehbare Preise. Abhängig von der Wohnungsgröße und dem Inventar kalkulieren wir ein individuelles Festpreisangebot für Sie.`,
      example: `Eine 3-Zimmer-Wohnung muss nach einem Auszug oder Todesfall komplett geräumt werden. Wir sichten das Inventar, rechnen eventuelle Wertgegenstände an und nennen Ihnen einen garantierten Festpreis für die komplette Räumung.`,
      why: `Als Familienbetrieb verzichten wir auf teure Subunternehmer. Unser eigenes Team arbeitet zügig und respektvoll. Zudem mindern wir die Kosten durch gezielte Wertanrechnung gut erhaltener Möbel.`,
      tip: `Machen Sie den ersten Schritt und kontaktieren Sie uns unverbindlich. Wir erstellen Ihnen zügig ein faires Angebot für die ${title}.`,
    };
  }
  if (t.includes("nachlassverwertung")) {
    return {
      intro: `Bei einem Nachlass stellt sich oft die Frage: <strong>Wie setzen sich die Kosten für eine ${title} in Berlin zusammen?</strong> Hier geht es nicht nur um Entsorgung, sondern vor allem um Werte. Wir bewerten transparent und rechnen fair an.`,
      example: `Sie haben ein Haus geerbt, in dem sich noch Antiquitäten, Schmuck oder gut erhaltene Möbel befinden. Wir schätzen diese Werte marktgerecht ein und verrechnen sie direkt mit den Räumungskosten. Oft sinkt dadurch der Preis erheblich.`,
      why: `Wir haben das geschulte Auge für Wertvolles. Statt alles ungesehen zu entsorgen, prüfen wir sorgfältig, was noch verkauft oder gespendet werden kann. Diese Expertise zahlt sich für Sie aus.`,
      tip: `Lassen Sie uns den Nachlass gemeinsam sichten. Wir beraten Sie kostenfrei und erstellen ein transparentes Angebot zur ${title}.`,
    };
  }
  if (t.includes("büroauflösung")) {
    return {
      intro: `Für Unternehmen ist Budgetplanung essenziell: <strong>Was kostet eine professionelle ${title} in Berlin?</strong> Wir bieten Geschäftskunden verlässliche Festpreise, damit Sie genau kalkulieren können, ohne spätere Überraschungen.`,
      example: `Ihre Büroräume müssen besenrein an den Vermieter übergeben werden. Wir demontieren Schreibtische, entsorgen alte IT fachgerecht (inkl. zertifizierter Aktenvernichtung) und garantieren einen reibungslosen Ablauf zum Festpreis.`,
      why: `Wir wissen, dass Zeit Geld ist. Unsere Teams arbeiten auf Wunsch auch am Wochenende oder nach Feierabend, um Ihren Betriebsablauf nicht zu stören. Effiziente Logistik hält unsere Preise attraktiv.`,
      tip: `Fordern Sie jetzt Ihr Firmenangebot an. Wir besichtigen Ihre Räume und liefern Ihnen umgehend einen Festpreis für die ${title}.`,
    };
  }
  if (t.includes("geschäftsauflösung")) {
    return {
      intro: `Bei der Schließung oder dem Umzug von Gewerberäumen ist Kostensicherheit wichtig: <strong>Wie berechnen sich die Kosten für eine ${title} in Berlin?</strong> Wir kalkulieren transparent nach Fläche, Inventar und speziellen Anforderungen.`,
      example: `Sie geben ein Ladenlokal oder eine Werkstatt auf. Wir kümmern uns um den Abbau von Regalsystemen, die Entsorgung von Sondermüll und die besenreine Übergabe an den Eigentümer – alles zu einem vorher vereinbarten Festpreis.`,
      why: `Gewerbliche Auflösungen erfordern spezielles Know-how. Wir bringen die richtige Ausrüstung mit und entsorgen fachgerecht nach gesetzlichen Vorgaben. Das schützt Sie vor rechtlichen Problemen und versteckten Kosten.`,
      tip: `Vereinbaren Sie einen kostenfreien Besichtigungstermin. Wir erstellen Ihnen ein verbindliches Angebot für Ihre ${title} in Berlin.`,
    };
  }

  return {
    intro: `Wahrscheinlich die Frage, die Ihnen schon seit der Überschrift auf der Zunge brennt: <strong>Was kostet eine ${title} in Berlin eigentlich?</strong> Bei Miros Haushaltsauflösung starten unsere Preise fair kalkuliert. Natürlich kann es je nach Größe und Aufwand variieren, aber unser Festpreis bleibt in jedem Fall transparent.`,
    example: `Sie haben eine Zweizimmerwohnung mit Keller in Berlin, die entrümpelt werden muss. Anhand Ihrer Fotos und Informationen kalkulieren wir genau und nennen Ihnen einen fairen Festpreis. In diesem Preis sind sämtliche Kosten für Abbau, Transport, Entsorgung und Personal enthalten.`,
    why: `Wir arbeiten effizient. Wir sind eingespielt, kennen die Anlaufstellen in Berlin und müssen keine Zeit mit unnötigen Wegen verschwenden. Effizienz spart Kosten – für uns und damit für Sie.`,
    tip: `Fragen Sie einfach kostenfrei und unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein transparentes Angebot für Ihre ${title} in Berlin zusammen.`,
  };
};

export function ServicePageTemplate({
  service,
}: {
  service: ServicePageProps;
}) {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://miros-haushaltsaufloesung.de/#localbusiness",
    name: "Miros Haushaltsauflösung",
    image: service.heroImg,
    description: service.metaDesc,
    url: `https://miros-haushaltsaufloesung.de/${service.slug}`,
    telephone: "+4915253181061",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Hauptstraße 43-44",
      addressLocality: "Berlin",
      postalCode: "13127",
      addressCountry: "DE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 52.5833,
      longitude: 13.4,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      opens: "08:00",
      closes: "20:00",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "500",
    },
    sameAs: [
      "https://www.google.com/maps/place/Miros+Haushaltsaufl%C3%B6sung/@52.7890372,12.4843482,9z/data=!3m1!4b1!4m6!3m5!1s0x23bf4a60473c084d:0x8a12d3b416c3d36a!8m2!3d52.7890372!4d12.4843482!16s%2Fg%2F11w12b1_n0",
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    provider: {
      "@id": "https://miros-haushaltsaufloesung.de/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Berlin",
    },
    description: service.metaDesc,
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "199.00",
        priceCurrency: "EUR",
        priceType: "Minimum",
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Startseite",
        item: "https://miros-haushaltsaufloesung.de/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: service.title,
        item: `https://miros-haushaltsaufloesung.de/${service.slug}`,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Wie setzen sich die Kosten zusammen?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Die Kosten hängen von der Menge des Räumguts, dem Stockwerk und der Zugänglichkeit ab. Wir bieten stets einen Festpreis an, der alle Kosten (Personal, Transport, Entsorgung) beinhaltet.",
        },
      },
      {
        "@type": "Question",
        name: "Wie schnell bekomme ich einen Termin?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Besichtigungstermine sind in der Regel innerhalb von 24 bis 48 Stunden möglich. Die eigentliche Räumung kann oft schon in derselben Woche stattfinden.",
        },
      },
      {
        "@type": "Question",
        name: "Muss ich bei der Räumung anwesend sein?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Nein, Sie müssen nicht vor Ort sein. Nach der Schlüsselübergabe und Vorbesprechung erledigen wir alles selbstständig und rufen Sie zur Endabnahme an.",
        },
      },
      {
        "@type": "Question",
        name: "Sind Sie versichert?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja, wir verfügen über eine umfassende Betriebshaftpflichtversicherung. Sollte beim Transport im Treppenhaus ein Schaden entstehen, sind Sie komplett abgesichert.",
        },
      },
      {
        "@type": "Question",
        name: "Was passiert mit Wertgegenständen?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Verwertbare Gegenstände wie Antiquitäten oder Schmuck schätzen wir fair ein und rechnen den Wert transparent gegen die Räumungskosten an.",
        },
      },
      {
        "@type": "Question",
        name: "Kümmern Sie sich um die Entsorgung?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja, wir trennen den anfallenden Müll fachgerecht und entsorgen ihn umweltfreundlich bei zertifizierten Berliner Recyclinghöfen.",
        },
      },
    ],
  };

  const schemas = [
    localBusinessSchema,
    serviceSchema,
    breadcrumbSchema,
    faqSchema,
  ];

  return (
    <>
      <SEOHead
        title={service.metaTitle}
        description={service.metaDesc}
        canonical={`https://miros-haushaltsaufloesung.de/${service.slug}`}
        schemaJson={schemas}
        ogImage={service.heroImg}
      />
      <div className="flex flex-col w-full">
        {/* 1. HERO SECTION */}
        <section className="relative w-full pt-32 md:pt-40 flex items-stretch bg-[#0c2340] text-white overflow-hidden min-h-[70vh]">
          <div className="absolute inset-0 z-0">
            <img
              src={service.heroImg}
              alt={service.keyword}
              width="1200"
              height="800"
              className="w-full h-full object-cover opacity-30"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-[#0c2340]/70"></div>
          </div>

          <div className="container relative z-20 mx-auto px-4 md:px-6 h-full flex flex-col justify-end pt-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-end h-full">
              <div className="text-left space-y-8 pb-20 md:pb-32">
                <nav
                  aria-label="Breadcrumb"
                  className="mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700"
                >
                  <ol className="flex items-center space-x-2 text-sm text-white/70">
                    <li>
                      <a
                        href="/"
                        className="hover:text-white transition-colors"
                      >
                        Startseite
                      </a>
                    </li>
                    <li>
                      <span className="mx-2">/</span>
                    </li>
                    <li className="text-white font-medium" aria-current="page">
                      {service.title}
                    </li>
                  </ol>
                </nav>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.1] tracking-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
                  {service.keyword}
                </h1>

                <p className="text-lg md:text-xl text-white/90 font-medium animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
                  {service.heroSub}
                </p>

                <div className="flex flex-col sm:flex-row gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">Festpreisgarantie</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">
                      Betriebshaftpflicht versichert
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">
                      Termine innerhalb 48 Stunden
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-lg hover:shadow-xl w-full sm:w-auto"
                      >
                        Kostenlose Besichtigung anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <a
                    href="tel:015253181061"
                    className="flex items-center gap-2 text-xl font-bold text-white hover:text-accent transition-colors"
                  >
                    <Phone className="h-6 w-6" /> 01525 3181061
                  </a>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-md lg:max-w-none flex justify-center lg:justify-end items-end h-full">
                <div className="relative inline-block w-full max-w-[550px] lg:max-w-[750px]">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3d1c2ddb2129be182b6f09.png"
                    alt="Miroslav Ozegovic"
                    width="400"
                    height="600"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-auto object-contain animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 relative z-10"
                  />
                  <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 w-[95%] max-w-[340px] bg-[#0f172a]/70 backdrop-blur-md border border-white/10 rounded-xl p-4 sm:p-5 text-center shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 delay-700 z-20">
                    <div className="text-white font-bold text-lg sm:text-xl mb-1">
                      Darf ich Sie beraten?
                    </div>
                    <a
                      href="tel:015253181061"
                      className="text-accent font-black text-2xl sm:text-3xl tracking-tight block hover:scale-105 transition-transform duration-200"
                    >
                      01525 3181061
                    </a>
                    <div className="flex items-center justify-center gap-2 mt-3 text-white/90 text-sm sm:text-base font-medium">
                      <span className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                      Mo.-Sa. 8 - 20 Uhr
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. VERTRAUENSSEKTION (WITH IMAGE) */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="space-y-8">
                <p className="text-xl md:text-2xl text-foreground font-medium leading-relaxed">
                  {service.trustIntro}
                </p>
                <div className="pt-8 border-t border-border">
                  <p className="text-lg italic text-muted-foreground font-medium">
                    "Herr Ozegovic hat uns in einer sehr schweren Zeit
                    unglaublich geholfen. Diskret, schnell und absolut
                    verlässlich. Man merkt sofort, dass hier Profis am Werk
                    sind, die auch menschlich überzeugen."
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-secondary/50 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src={service.trustImg}
                  alt="Vertrauensvolle Zusammenarbeit"
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/3] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 3. LEISTUNGSÜBERSICHT */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-center mb-16">
              Was wir für Sie übernehmen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {allServices.map((item, i) => (
                <a
                  key={i}
                  href={`/${item.id}`}
                  className="bg-background p-8 rounded-sm border border-border shadow-sm hover:shadow-md transition-all group block"
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-6 line-clamp-2">
                    {item.desc}
                  </p>
                  <div className="text-primary font-bold flex items-center group-hover:translate-x-2 transition-transform">
                    Mehr erfahren <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 4. ABLAUF */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                So läuft eine {service.title} mit Miros Haushaltsauflösung ab
              </h2>
            </div>
            <div className="max-w-4xl mx-auto relative">
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-border transform md:-translate-x-1/2 hidden sm:block"></div>
              <div className="space-y-12">
                {[
                  {
                    title: "1. Kostenlose Besichtigung vor Ort",
                    desc: "Wir kommen zeitnah zu Ihnen, um uns ein genaues Bild vom Umfang der Arbeiten zu machen und alle Details zu besprechen.",
                  },
                  {
                    title: "2. Verbindliches Festpreisangebot",
                    desc: "Auf Basis der Besichtigung erhalten Sie ein transparentes Angebot mit Festpreisgarantie. Keine versteckten Kosten.",
                  },
                  {
                    title: "3. Räumung zum vereinbarten Termin",
                    desc: "Unser erfahrenes Team arbeitet zügig, diskret und professionell. Wir kümmern uns um alles, inklusive Halteverbot.",
                  },
                  {
                    title: "4. Besenreine Übergabe",
                    desc: "Wir hinterlassen die Räumlichkeiten sauber und übergabefertig für Sie, den Vermieter oder Käufer.",
                  },
                ].map((step, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col sm:flex-row items-center gap-8 ${idx % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                  >
                    <div className="absolute left-8 md:left-1/2 top-8 md:top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl border-4 border-background z-10 hidden sm:flex shadow-sm">
                      {idx + 1}
                    </div>
                    <div
                      className={`w-full sm:w-1/2 ${idx % 2 === 1 ? "md:pr-12" : "md:pl-12"} flex ${idx % 2 === 1 ? "justify-start" : "justify-end"}`}
                    >
                      <div className="bg-secondary/30 p-8 rounded-sm border border-border shadow-sm w-full relative">
                        <div className="flex items-center gap-4 mb-4 sm:hidden">
                          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg border-2 border-background shadow-sm shrink-0">
                            {idx + 1}
                          </div>
                          <h3 className="text-xl font-bold text-foreground m-0">
                            {step.title.substring(3)}
                          </h3>
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-3 hidden sm:block">
                          {step.title.substring(3)}
                        </h3>
                        <p className="text-muted-foreground">{step.desc}</p>
                      </div>
                    </div>
                    <div className="hidden sm:block w-1/2"></div>
                  </div>
                ))}
              </div>
              <div className="mt-16 text-center">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md"
                    >
                      Jetzt Termin anfragen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                    <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                      <DialogHeader className="mb-5">
                        <DialogTitle className="text-xl sm:text-2xl font-bold">
                          Kostenloses Angebot anfordern
                        </DialogTitle>
                      </DialogHeader>
                      <ContactForm />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </section>

        {/* OUR WORK SECTION (Conditional) */}
        {service.showOurWork && <OurWorkSection />}

        {/* 5. VERTRAUENSSIGNALE */}
        <section className="py-20 md:py-32 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-center mb-16">
              Warum Kunden Miros Haushaltsauflösung vertrauen
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {[
                {
                  title: "Festpreisgarantie",
                  desc: "Keine Nachzahlungen, keine versteckten Kosten. Der vereinbarte Preis steht.",
                  icon: <CheckCircle2 className="h-8 w-8" />,
                },
                {
                  title: "Betriebshaftpflicht",
                  desc: "Im unwahrscheinlichen Fall eines Schadens sind Sie voll abgesichert.",
                  icon: <Shield className="h-8 w-8" />,
                },
                {
                  title: "Familienunternehmen",
                  desc: "Persönliche Betreuung vom Chef selbst. Wir arbeiten mit Herz und Verstand.",
                  icon: <Star className="h-8 w-8" />,
                },
                {
                  title: "Diskrete Arbeitsweise",
                  desc: "Wir arbeiten leise und unauffällig, aus Respekt vor Ihnen und den Nachbarn.",
                  icon: <CheckCircle2 className="h-8 w-8" />,
                },
              ].map((usp, idx) => (
                <div
                  key={idx}
                  className="bg-primary-foreground/10 p-8 rounded-sm backdrop-blur-sm border border-primary-foreground/20 text-center"
                >
                  <div className="flex justify-center mb-4 text-accent">
                    {usp.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{usp.title}</h3>
                  <p className="opacity-90">{usp.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <div>
                  <div className="text-primary font-bold tracking-wide uppercase mb-2">
                    Transparente Preise
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                    Was kostet eine{" "}
                    <span className="text-primary">{service.title}</span> in
                    Berlin?
                  </h2>
                </div>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: getPricingContent(service.title).intro,
                    }}
                  ></p>

                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Beispiel:</strong>{" "}
                      {getPricingContent(service.title).example}
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">
                        Warum unsere Preise fair sind:
                      </strong>{" "}
                      {getPricingContent(service.title).why}
                    </div>
                  </div>

                  <p className="font-medium text-foreground">
                    <strong>Unser Tipp:</strong>{" "}
                    {getPricingContent(service.title).tip}
                  </p>
                </div>
              </div>

              {/* Right Column: Pricing Box */}
              <div className="relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="absolute -inset-4 bg-primary/10 rounded-sm transform rotate-2 z-0"></div>
                <div className="relative z-10 bg-primary text-primary-foreground p-8 md:p-10 rounded-sm shadow-xl flex flex-col items-center text-center">
                  <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-xl font-medium">ab</span>
                    <span className="text-5xl md:text-6xl font-heading font-black text-accent">
                      199 €
                    </span>
                  </div>

                  <p className="font-bold mb-6 opacity-90">abhängig von</p>

                  <ul className="space-y-4 mb-8 w-full text-left">
                    {[
                      "Fläche in QM",
                      "Menge des Hausrats",
                      "Zusatzleistungen (z.B. Bodenbeläge)",
                      "Stockwerk & Aufzug",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="w-full h-14 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Genaue Kosten anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto text-foreground">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>

                  <p className="text-sm mt-4 opacity-80">
                    *kostenfrei und unverbindlich
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <div className="relative order-2 lg:order-1">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src={service.seoImg}
                  alt="Einsatz in ganz Berlin"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
              <div className="space-y-8 order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground">
                  {service.title} in ganz Berlin
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Wir sind in allen Berliner Bezirken für Sie im Einsatz. Ob in
                  den engen Altbau-Treppenhäusern von Prenzlauer Berg, Kreuzberg
                  und Friedrichshain, bei Parkplatzknappheit in Mitte und
                  Charlottenburg, oder in den weitläufigeren Gebieten wie
                  Steglitz, Tempelhof, Neukölln, Wedding und Pankow. Wir kennen
                  die typischen Berliner Herausforderungen genau. Wir beantragen
                  notwendige Halteverbotszonen, kümmern uns um den sicheren
                  Abtransport auch aus oberen Stockwerken ohne Aufzug und
                  garantieren eine reibungslose Abwicklung.
                </p>
                <div className="flex flex-wrap gap-3 pt-4">
                  {[
                    { name: "Berlin", slug: "berlin" },
                    { name: "Pankow", slug: "pankow" },
                    { name: "Oberhavel", slug: "oberhavel" },
                    {
                      name: "Marzahn-Hellersdorf",
                      slug: "marzahn-hellersdorf",
                    },
                    { name: "Treptow Köpenick", slug: "treptow-koepenick" },
                    { name: "Spandau", slug: "spandau" },
                    { name: "Reinickendorf", slug: "reinickendorf" },
                    { name: "Neukölln", slug: "neukoelln" },
                    {
                      name: "Steglitz Zehlendorf",
                      slug: "steglitz-zehlendorf",
                    },
                    { name: "Märkisch Oderland", slug: "maerkisch-oderland" },
                    { name: "Charlottenburg", slug: "charlottenburg" },
                    { name: "Potsdam", slug: "potsdam" },
                    { name: "Oranienburg", slug: "oranienburg" },
                  ].map((bezirk) => (
                    <a
                      key={bezirk.name}
                      href={`/${bezirk.slug}`}
                      className="px-4 py-2 bg-secondary/50 rounded-full text-sm font-medium border border-border hover:bg-primary hover:text-primary-foreground transition-colors group"
                    >
                      <MapPin className="inline-block w-4 h-4 mr-1 text-primary group-hover:text-primary-foreground transition-colors" />
                      {bezirk.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. KUNDENSTIMMEN */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                Das sagen unsere Kunden
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 max-w-6xl mx-auto">
              {[
                {
                  text: "Sehr professionell, pünktlich und schnell. Die Wohnung wurde komplett besenrein hinterlassen. Absolute Empfehlung!",
                  author: "Kunde aus Pankow",
                },
                {
                  text: "Herr Ozegovic und sein Team haben tolle Arbeit geleistet. Der Festpreis wurde genau eingehalten. Keine versteckten Kosten.",
                  author: "Kundin aus Charlottenburg",
                },
                {
                  text: "Schnelle Terminvergabe und reibungsloser Ablauf. Sehr freundlicher Kontakt und absolut diskret.",
                  author: "Familie aus Spandau",
                },
              ].map((review, idx) => (
                <div
                  key={idx}
                  className="bg-background p-8 rounded-sm border border-border shadow-sm flex flex-col"
                >
                  <div className="flex gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="h-5 w-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-lg font-medium mb-8 leading-relaxed flex-grow">
                    "{review.text}"
                  </p>
                  <div className="font-bold opacity-90">{review.author}</div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <a
                href="https://g.page/r/CWrTwxa00xKKEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-bold hover:underline inline-flex items-center"
              >
                Alle Bewertungen auf Google ansehen{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* 8. FAQ SEKTION (WITH IMAGE) */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src={service.faqImg}
                  alt="Häufig gestellte Fragen"
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
              <div className="space-y-8">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold">
                  Häufige Fragen zur {service.title}
                </h2>
                <Accordion
                  type="single"
                  collapsible
                  className="w-full bg-background rounded-sm border border-border shadow-sm p-2"
                >
                  <AccordionItem value="item-1" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Wie setzen sich die Kosten zusammen?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Die Kosten hängen von der Menge des Räumguts, dem
                      Stockwerk und der Zugänglichkeit ab. Wir bieten stets
                      einen Festpreis an, der alle Kosten (Personal, Transport,
                      Entsorgung) beinhaltet.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-2" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Wie schnell bekomme ich einen Termin?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Besichtigungstermine sind in der Regel innerhalb von 24
                      bis 48 Stunden möglich. Die eigentliche Räumung kann oft
                      schon in derselben Woche stattfinden.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-3" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Muss ich bei der Räumung anwesend sein?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Nein, Sie müssen nicht vor Ort sein. Nach der
                      Schlüsselübergabe und Vorbesprechung erledigen wir alles
                      selbstständig und rufen Sie zur Endabnahme an.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-4" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Sind Sie versichert?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Ja, wir verfügen über eine umfassende
                      Betriebshaftpflichtversicherung. Sollte beim Transport im
                      Treppenhaus ein Schaden entstehen, sind Sie komplett
                      abgesichert.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-5" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Was passiert mit Wertgegenständen?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Verwertbare Gegenstände wie Antiquitäten oder Schmuck
                      schätzen wir fair ein und rechnen den Wert transparent
                      gegen die Räumungskosten an.
                    </AccordionContent>
                  </AccordionItem>
                  <div className="h-px bg-border mx-4"></div>
                  <AccordionItem value="item-6" className="border-b-0">
                    <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                      Kümmern Sie sich um die Entsorgung?
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                      Ja, wir trennen den anfallenden Müll fachgerecht und
                      entsorgen ihn umweltfreundlich bei zertifizierten Berliner
                      Recyclinghöfen.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        {/* 9. ABSCHLUSS CTA */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto bg-background rounded-sm border border-border shadow-xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                Kostenlose Besichtigung anfragen
              </h2>
              <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                Kein Risiko, keine Verpflichtung. Hinterlassen Sie uns Ihre
                Daten und wir melden uns noch am selben Tag bei Ihnen zurück.
              </p>
              <div className="max-w-xl mx-auto text-left">
                <ContactForm />
              </div>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-muted-foreground">
                <span className="font-medium">
                  Oder rufen Sie uns direkt an:
                </span>
                <a
                  href="tel:015253181061"
                  className="flex items-center gap-2 text-2xl font-bold text-primary hover:text-accent transition-colors"
                >
                  <Phone className="h-6 w-6" /> 01525 3181061
                </a>
              </div>
              <div className="mt-12 pt-8 border-t border-border text-sm font-medium text-muted-foreground flex flex-wrap justify-center gap-4">
                <span>Kostenlose Besichtigung.</span>
                <span>Verbindlicher Festpreis.</span>
                <span>Diskrete Abwicklung.</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
